import { useContext, useMemo } from 'react'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import {
  getMinimumDurationFromFlexibleNights,
  getShouldUseSearchPrices,
} from 'lib/search/searchUtils'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import getLoyaltyEarnKey, { getLoyaltyBedbankPackageId } from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import { FlightsClassTypes, isRewardFareClass } from 'constants/flight'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { useMetaSearchMobileV2Enabled } from 'components/Pages/HotelSearchPage/useMetaSearchMobileV2'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  bestPackage?: App.HotelPackage
  bestAvailableRate?: App.OfferAvailableRate
  filters?: App.OfferListFilters
  metaData?: App.OfferListMetaData
  size?: 'S' | 'M'
}

function HotelSearchTileEarnablePoints(props: Props) {
  const { offer, bestPackage, bestAvailableRate, filters, metaData, size = 'M' } = props
  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const duration = filters?.flexibleMonths && filters.flexibleNights ? getMinimumDurationFromFlexibleNights(filters.flexibleNights as FLEXIBLE_DURATION_RANGE) : offer.lowestPricePackage?.duration
  const defaultAirport = useDefaultAirport()
  const flightPrice = (offer.flightPrices[defaultAirport?.code ?? ''] || 0)

  const {
    defaultPricing,
    memberPricing,
  } = useSearchPrices({
    offer,
    pkg: bestPackage,
    rate: bestAvailableRate,
    duration,
    filters,
    useRealSearchPrices: shouldUseRealSearchPrices,
  })

  const defaultPrice = defaultPricing.price
  const memberPrice = memberPricing?.price ?? 0

  const propertyFees = defaultPricing.fees?.find((fee) => fee.type === 'property')?.amount ?? 0

  const defaultPriceExcludingPropertyFees = defaultPrice - propertyFees
  const memberPriceExcludingPropertyFees = memberPrice > 0 ? memberPrice - propertyFees : 0

  const { currentRegionCode } = useContext(GeoContext)
  const { selectedFareClass } = useMaldivesDirectBundleContext()
  const isRewardSelected = !!selectedFareClass && isRewardFareClass(selectedFareClass)
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const isCharterBundle = !!charterBundleRoute
  const rooms = filters?.rooms ?? [{ adults: 2 }]
  const { totalPrice: charterFlightPrice, loyaltyEarnKey: charterLoyaltyEarnKey } = useCharterFlightPrice({
    charterBundleRoute: charterBundleRoute ?? 'melbourne-maldives',
    checkInDate: filters?.checkIn,
    checkOutDate: filters?.checkOut,
    occupancies: rooms,
    fareClass: selectedFareClass ?? FlightsClassTypes.ECONOMY,
    disabled: !isCharterBundle,
  })

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>((): Array<App.LoyaltyPointsEarnCalculationRequest | undefined> => {
    const isBedbankRateType = metaData?.pricing?.rateType === 'bedbank'

    /**
     * @NOTE - this logic here is because of the SPP (single property page).
     */
    if (metaData && isBedbankRateType) {
      return [
        {
          price: metaData.pricing?.lowestPrice ?? 0,
          memberPrice: metaData?.pricing?.lowestMemberPrice ?? 0,
          mx: metaData?.pricing?.mx,
          loyaltyProductType: 'hotel_lpp_eps',
          packageId: getLoyaltyBedbankPackageId(metaData.pricing.ratePropertyId, metaData.pricing.rateSupplierCode),
          offerId: metaData.pricing.ratePropertyId ?? '',
          earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode: offer.property.geoData.countryCode, propertyStateProvince: offer.property.geoData.administrativeAreaLevel1, currentRegionCode, propertyId: metaData.pricing.ratePropertyId, supplierCode: metaData.pricing.rateSupplierCode } }),
        },
        generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
          flightPrice,
          skip: !offer.bundledWithFlightsOnly || isRewardSelected,
        }),
      ]
    }

    // Split the combined price evenly per room so each room is priced and rounded separately then
    // summed, matching the offer page/checkout, instead of rounding the combined total once. Exact
    // when rooms share the same package/rate (the common case); still an approximation if the
    // customer later picks different rooms on the offer page.
    const shouldSplitPerRoom = !isCharterBundle && rooms.length > 1
    const roomCount = shouldSplitPerRoom ? rooms.length : 1
    const hotelEarnPrice = isCharterBundle ? (bestPackage?.price ?? 0) : defaultPriceExcludingPropertyFees / roomCount
    const hotelEarnMemberPrice = isCharterBundle ? (bestPackage?.memberPrice ?? 0) : memberPriceExcludingPropertyFees / roomCount

    const hotelEarnRequests = Array.from({ length: roomCount }, () => generateLoyaltyPointsCalculatorHotelOptions(offer, {
      pkg: bestPackage,
      price: hotelEarnPrice,
      memberPrice: hotelEarnMemberPrice,
      currentRegionCode,
      nights: bestPackage?.duration,
    }))

    // For charter bundles, split earn into hotel (pkg.price) + flight (charterFlightPrice),
    // matching the offer page which uses packageView.pricing.price + effectiveFlightPrice.
    // For reward cabins, skip flight earn (flights paid with points, not earnable).
    return [
      ...hotelEarnRequests,
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice: isCharterBundle ? (charterFlightPrice ?? 0) : flightPrice,
        loyaltyEarnKey: isCharterBundle ? charterLoyaltyEarnKey : undefined,
        skip: isCharterBundle ? isRewardSelected : !offer.bundledWithFlightsOnly,
      }),
    ]
  }, [bestPackage, rooms.length, currentRegionCode, defaultPriceExcludingPropertyFees, flightPrice, isRewardSelected, isCharterBundle, charterFlightPrice, charterLoyaltyEarnKey, memberPriceExcludingPropertyFees, metaData, offer])

  const hotelProductType = getLoyaltyProductType(offer)
  const burnProductType = (hotelProductType === 'hotel_flash' || hotelProductType === 'hotel_lpc') ? hotelProductType : undefined
  const { hasSelectedBurnToggle } = useSearchBurnEnabled()
  const viewPriceInPoints = !!filters?.viewPriceInPoints && !hasSelectedBurnToggle
  const burnResult = useSearchBurnPoints(defaultPrice, burnProductType, getHotelEncryptedMargin(offer.type, bestPackage), { viewPriceInPoints })
  const hideEarnLine = useMetaSearchMobileV2Enabled() && isFlashHotelOffer(offer)

  if (hideEarnLine) return null

  return <LoyaltyPointsEarn
    size={size}
    calculationRequests={pointsEarnCalculationRequests}
    calculationType="estimate"
    offer={offer}
    pointsToApply={burnResult.pointsToBurn}
    burnPriceInPoints={burnResult.priceInPoints}
  />
}

export default HotelSearchTileEarnablePoints
